import React from 'react'
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'

const STORAGE_KEY = 'wg-anzeigen_login_target'

const Login: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { authenticated, ready, login, error } = useAuth()
  const [submitting, setSubmitting] = React.useState(false)
  const [localError, setLocalError] = React.useState<string | null>(null)

  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')

  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search])
  const redirectParam = searchParams.get('redirect') || '/new'
  const redirect = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return redirectParam.startsWith('/') ? redirectParam : `/${redirectParam}`
    }
    try {
      const url = new URL(redirectParam, window.location.origin)
      if (url.origin !== window.location.origin) {
        return '/new'
      }
      const relative = `${url.pathname}${url.search}${url.hash}`
      return relative || '/new'
    } catch {
      return redirectParam.startsWith('/') ? redirectParam : `/${redirectParam}`
    }
  }, [redirectParam])

  React.useEffect(() => {
    if (!ready || !authenticated) return
    let target = redirect
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (stored) {
        sessionStorage.removeItem(STORAGE_KEY)
        target = stored
      }
    } catch {
      // ignore
    }
    navigate(target, { replace: true })
  }, [authenticated, ready, navigate, redirect])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setLocalError(null)
    try {
      try {
        sessionStorage.setItem(STORAGE_KEY, redirect)
      } catch {
        // ignore
      }
      await login(username, password)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.loginError', 'Login fehlgeschlagen')
      setLocalError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = React.useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/', { replace: true })
    }
  }, [navigate])

  const busy = submitting || !ready
  const combinedError = localError || error

  const benefits = React.useMemo(
    () => [
      {
        icon: <HomeWorkOutlinedIcon fontSize="small" />,
        title: t('auth.loginBenefitListingsTitle', 'Alle Inserate im Blick'),
        description: t(
          'auth.loginBenefitListingsDescription',
          'Bearbeite bestehende Anzeigen oder starte neue Gesuche in wenigen Klicks.'
        )
      },
      {
        icon: <ShieldOutlinedIcon fontSize="small" />,
        title: t('auth.loginBenefitSecurityTitle', 'Vertrauen & Sicherheit'),
        description: t(
          'auth.loginBenefitSecurityDescription',
          'Sichere Kommunikation mit verifizierten Kontakten aus der WG-Anzeigen-Community.'
        )
      },
      {
        icon: <SupportAgentOutlinedIcon fontSize="small" />,
        title: t('auth.loginBenefitSupportTitle', 'Persönlicher Support'),
        description: t(
          'auth.loginBenefitSupportDescription',
          'Unser Team hilft dir bei Fragen zu deinem Inserat oder zur Plattform.'
        )
      }
    ],
    [t]
  )

  return (
    <Box
      sx={(theme) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
        py: { xs: 4, md: 8 }
      })}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Box
              sx={(theme) => ({
                position: 'relative',
                borderRadius: 4,
                p: { xs: 4, md: 5 },
                background:
                  theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                    : 'linear-gradient(135deg, #e17055 0%, #fdcb6e 100%)',
                color: theme.palette.getContrastText(theme.palette.primary.main),
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 4, md: 6 },
                flex: 1,
                overflow: 'hidden'
              })}
            >
              <Box>
                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1.5 }}>
                  {t('auth.loginHeroEyebrow', 'Gemeinsam wohnen, gemeinsam sparen')}
                </Typography>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{ fontWeight: 700, mt: 1, mb: 2, lineHeight: 1.1 }}
                >
                  {t('auth.loginHeroTitle', 'Willkommen zurück bei WG-Anzeigen')}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.92, fontWeight: 400 }}>
                  {t(
                    'auth.loginHeroSubtitle',
                    'Verwalte deine Inserate und finde passende Mitbewohner:innen in einer Oberfläche.'
                  )}
                </Typography>
              </Box>

              <Stack spacing={3}>
                {benefits.map((benefit) => (
                  <Stack
                    key={benefit.title}
                    direction="row"
                    spacing={2}
                    alignItems="flex-start"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.14)',
                      backdropFilter: 'blur(8px)'
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.24)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {benefit.icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {benefit.description}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>

              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {t(
                  'auth.loginHeroFooter',
                  'Dein Zugang zu seriösen WG-Angeboten in ganz Deutschland.'
                )}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'stretch' }}>
            <Paper
              elevation={4}
              sx={{
                p: { xs: 3, md: 4 },
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
              component="form"
              onSubmit={handleSubmit}
            >
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" component="h2" gutterBottom>
                      {t('auth.loginTitle', 'Anmelden')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t('auth.loginDescription', 'Melde dich an, um Anzeigen zu erstellen und zu verwalten.')}
                    </Typography>
                  </Box>
                  <IconButton
                    edge="end"
                    aria-label={t('common.close', 'Schließen')}
                    onClick={handleClose}
                    size="large"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Stack spacing={2}>
                  {combinedError && <Alert severity="error">{combinedError}</Alert>}
                  
                  <TextField
                    id="username"
                    name="username"
                    label={t('auth.usernameOrEmail', 'E-Mail oder Benutzername')}
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    fullWidth
                    autoComplete="username"
                    disabled={busy}
                  />

                  <TextField
                    id="password"
                    name="password"
                    label={t('auth.password', 'Passwort')}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    autoComplete="current-password"
                    disabled={busy}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Link
                      component={RouterLink}
                      to="/forgot-password"
                      variant="body2"
                      color="primary"
                    >
                      {t('auth.forgotPassword', 'Passwort vergessen?')}
                    </Link>
                  </Box>
                </Stack>

                <Button type="submit" size="large" variant="contained" disabled={busy}>
                  {busy ? t('auth.loggingIn', 'Weiterleitung…') : t('auth.loginButton', 'Anmelden')}
                </Button>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('auth.noAccountYet', 'Noch keinen Account?')}
                  </Typography>
                  <Link
                    component={RouterLink}
                    to={`/register?redirect=${encodeURIComponent(redirect)}`}
                    variant="body2"
                  >
                    {t('auth.registerCta', 'Jetzt registrieren')}
                  </Link>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default Login
